import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiX, FiSend, FiEdit3, FiCheck, FiAlertCircle,
  FiSearch, FiChevronRight, FiMail, FiEye,
  FiCheckCircle, FiRefreshCw, FiRotateCcw, FiZap, FiCode,
} from 'react-icons/fi';
import {
  MdFormatBold, MdFormatItalic, MdFormatUnderlined, MdFormatStrikethrough,
  MdFormatListBulleted, MdFormatListNumbered, MdLink, MdFormatClear,
  MdFormatQuote,
} from 'react-icons/md';
import axios from 'axios';
import toast from 'react-hot-toast';

// ─── Tone options (matches AIEmailHelper) ─────────────────────────────────────
const TONES = [
  { id: 'Professional', emoji: '💼' },
  { id: 'Friendly',     emoji: '😊' },
  { id: 'Persuasive',   emoji: '🎯' },
  { id: 'Concise',      emoji: '✂️' },
  { id: 'Formal',       emoji: '🎩' },
];

// ─── Rich Body Editor ─────────────────────────────────────────────────────────
// Uses an <iframe> with designMode="on" so the FULL HTML document (including
// <head> and <style> tags) is preserved on save — unlike contentEditable which
// strips DOCTYPE / head wrappers and loses all CSS, causing blank headers in
// email clients.
const RichBodyEditor = ({ value, onSaveClose }) => {
  const [mode, setMode]           = useState('visual'); // 'visual' | 'html'
  const [htmlDraft, setHtmlDraft] = useState(value || '');
  const [aiTone, setAiTone]       = useState('Professional');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult]   = useState(null);
  const iframeRef                 = useRef(null);

  // ── iframe helpers ───────────────────────────────────────────────────────
  const writeToIframe = useCallback((html) => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(html || '<html><body><p></p></body></html>');
    doc.close();
    doc.designMode = 'on';
  }, []);

  const readFromIframe = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return htmlDraft;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return htmlDraft;
    try {
      // Return only the body content — NOT the full document.
      // The template wrapper (header, logo, CTA, footer) is always applied server-side
      // via templateId. Returning a full <!DOCTYPE html> document here would cause
      // isFullHtmlDoc() to drop the templateId and send the email without the template.
      return doc.body ? doc.body.innerHTML : htmlDraft;
    } catch {
      return htmlDraft;
    }
  }, [htmlDraft]);

  // Seed iframe on first mount
  useEffect(() => {
    // Small delay so the iframe DOM is ready
    const t = setTimeout(() => writeToIframe(value || ''), 80);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Mode switching ───────────────────────────────────────────────────────
  const switchMode = (newMode) => {
    if (newMode === 'html' && mode === 'visual') {
      // Visual → HTML: serialize iframe → textarea
      setHtmlDraft(readFromIframe());
    } else if (newMode === 'visual' && mode === 'html') {
      // HTML → Visual: write textarea → iframe
      setTimeout(() => writeToIframe(htmlDraft), 80);
    }
    setMode(newMode);
  };

  // ── Toolbar exec in iframe ───────────────────────────────────────────────
  const exec = (cmd, arg = null) => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;
    try { iframe.contentWindow.focus(); doc.execCommand(cmd, false, arg); } catch (_) {}
  };

  const insertLink = () => {
    const url = window.prompt('Enter URL:', 'https://');
    if (url) exec('createLink', url);
  };

  // ── Save: read from active editor, pass full HTML directly to parent ─────
  const handleSave = () => {
    const finalHtml = mode === 'visual' ? readFromIframe() : htmlDraft;
    onSaveClose(finalHtml); // passes value directly — no async state race
  };

  // ── AI Rephrase ──────────────────────────────────────────────────────────
  const handleAIRephrase = async () => {
    const currentBody = mode === 'visual' ? readFromIframe() : htmlDraft;
    if (!currentBody) { toast.error('Write something first'); return; }
    setAiLoading(true); setAiResult(null);
    try {
      const res = await axios.post('/api/ai/rephrase-email', {
        subject: '',
        body: currentBody,
        tone: aiTone,
      });
      setAiResult(res.data.result);
    } catch (err) {
      toast.error(`AI rephrase failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setAiLoading(false);
    }
  };

  const applyAI = () => {
    if (aiResult?.rephrasedHtml) {
      const newHtml = aiResult.rephrasedHtml;
      setHtmlDraft(newHtml);
      if (mode === 'visual') writeToIframe(newHtml);
      setAiResult(null);
      toast.success('AI content applied — click Save & Preview to confirm');
    }
  };

  // ── Toolbar helpers ──────────────────────────────────────────────────────
  const TBtn = ({ onClick, title, children }) => (
    <button
      type="button"
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      title={title}
      className="p-1.5 rounded text-sm text-neutral-600 hover:bg-neutral-200 transition-all flex-shrink-0"
    >
      {children}
    </button>
  );
  const TDivider = () => <div className="w-px h-5 bg-neutral-300 mx-0.5 flex-shrink-0" />;

  return (
    <div className="flex flex-col border border-violet-300 rounded-xl overflow-hidden bg-white">

      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-violet-200 bg-violet-50 flex-wrap">
        <TBtn onClick={() => exec('bold')}             title="Bold"><MdFormatBold className="w-4 h-4" /></TBtn>
        <TBtn onClick={() => exec('italic')}           title="Italic"><MdFormatItalic className="w-4 h-4" /></TBtn>
        <TBtn onClick={() => exec('underline')}        title="Underline"><MdFormatUnderlined className="w-4 h-4" /></TBtn>
        <TBtn onClick={() => exec('strikeThrough')}    title="Strikethrough"><MdFormatStrikethrough className="w-4 h-4" /></TBtn>
        <TDivider />
        <TBtn onClick={() => exec('formatBlock','H2')} title="Heading 2"><span className="text-xs font-bold">H2</span></TBtn>
        <TBtn onClick={() => exec('formatBlock','H3')} title="Heading 3"><span className="text-xs font-bold">H3</span></TBtn>
        <TBtn onClick={() => exec('formatBlock','p')}  title="Paragraph"><span className="text-xs">P</span></TBtn>
        <TDivider />
        <TBtn onClick={() => exec('insertUnorderedList')} title="Bullet list"><MdFormatListBulleted className="w-4 h-4" /></TBtn>
        <TBtn onClick={() => exec('insertOrderedList')}   title="Numbered list"><MdFormatListNumbered className="w-4 h-4" /></TBtn>
        <TBtn onClick={() => exec('formatBlock','blockquote')} title="Blockquote"><MdFormatQuote className="w-4 h-4" /></TBtn>
        <TDivider />
        <TBtn onClick={insertLink}                    title="Insert link"><MdLink className="w-4 h-4" /></TBtn>
        <TBtn onClick={() => exec('removeFormat')}    title="Clear formatting"><MdFormatClear className="w-4 h-4" /></TBtn>

        {/* Visual / HTML toggle */}
        <div className="ml-auto flex items-center gap-1 border border-violet-300 rounded-lg overflow-hidden">
          <button type="button" onClick={() => switchMode('visual')}
            className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium transition-all ${
              mode === 'visual' ? 'bg-violet-600 text-white' : 'text-neutral-600 hover:bg-violet-100'}`}>
            <FiEye className="w-3 h-3" /> Visual
          </button>
          <button type="button" onClick={() => switchMode('html')}
            className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium transition-all ${
              mode === 'html' ? 'bg-violet-600 text-white' : 'text-neutral-600 hover:bg-violet-100'}`}>
            <FiCode className="w-3 h-3" /> HTML
          </button>
        </div>
      </div>

      {/* Editor area */}
      {/* The iframe is always mounted so designMode persists; hidden when in HTML mode */}
      <div style={{ display: mode === 'visual' ? 'block' : 'none' }}>
        <iframe
          ref={iframeRef}
          title="Visual Email Editor"
          style={{ width: '100%', height: '300px', border: 'none', display: 'block' }}
        />
      </div>
      {mode === 'html' && (
        <textarea
          value={htmlDraft}
          onChange={e => setHtmlDraft(e.target.value)}
          rows={9}
          spellCheck={false}
          className="w-full p-3 text-xs font-mono text-neutral-700 focus:outline-none resize-none"
          placeholder="<p>Edit raw HTML here…</p>"
        />
      )}

      {/* AI Rephrase */}
      <div className="border-t border-violet-200 bg-gradient-to-r from-violet-50 to-purple-50 p-3">
        <div className="flex items-center gap-2 mb-2">
          <FiZap className="w-3.5 h-3.5 text-violet-600 flex-shrink-0" />
          <span className="text-xs font-bold text-violet-800">AI Rephrase</span>
          <span className="text-[10px] text-violet-500">— rewrites this recipient's content with AI</span>
        </div>
        <div className="flex gap-1.5 flex-wrap mb-2.5">
          {TONES.map(t => (
            <button key={t.id} type="button" onClick={() => setAiTone(t.id)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all border ${
                aiTone === t.id
                  ? 'bg-violet-600 text-white border-violet-600 shadow-sm'
                  : 'bg-white text-neutral-600 border-neutral-300 hover:border-violet-400 hover:text-violet-700'
              }`}>
              <span>{t.emoji}</span> {t.id}
            </button>
          ))}
        </div>
        <button type="button" onClick={handleAIRephrase} disabled={aiLoading}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white text-xs font-semibold rounded-lg hover:from-violet-700 hover:to-purple-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-sm">
          {aiLoading ? <><FiRefreshCw className="w-3.5 h-3.5 animate-spin" /> Rephrasing…</> : <><FiZap className="w-3.5 h-3.5" /> Rephrase with AI</>}
        </button>

        {aiResult && (
          <div className="mt-3 border border-violet-300 rounded-xl overflow-hidden bg-white shadow-sm">
            <div className="flex items-center justify-between px-3 py-2 bg-violet-100 border-b border-violet-200">
              <span className="text-xs font-bold text-violet-800">✨ AI Suggestion</span>
              <button type="button" onClick={() => setAiResult(null)} className="text-violet-500 hover:text-violet-700"><FiX className="w-3 h-3" /></button>
            </div>
            {aiResult.explanation && <p className="text-[11px] text-violet-700 px-3 pt-2 italic">"{aiResult.explanation}"</p>}
            <div className="px-3 py-2 text-sm max-h-40 overflow-y-auto" dangerouslySetInnerHTML={{ __html: aiResult.rephrasedHtml }} />
            <div className="flex gap-2 px-3 pb-3 pt-1">
              <button type="button" onClick={applyAI}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white text-xs font-semibold rounded-lg hover:bg-violet-700 transition-all">
                <FiCheck className="w-3 h-3" /> Apply
              </button>
              <button type="button" onClick={() => setAiResult(null)}
                className="px-3 py-1.5 bg-white border border-neutral-300 text-neutral-600 text-xs rounded-lg hover:bg-neutral-50 transition-all">
                Discard
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex gap-2 px-3 py-2.5 border-t border-violet-200 bg-white">
        <button type="button" onClick={handleSave}
          className="flex items-center gap-1.5 px-4 py-1.5 bg-violet-600 text-white text-xs font-semibold rounded-lg hover:bg-violet-700 transition-all">
          <FiCheck className="w-3.5 h-3.5" /> Save &amp; Preview
        </button>
      </div>
    </div>
  );
};

// ─── Main EmailPreview ────────────────────────────────────────────────────────
const EmailPreview = ({
  recipients,
  subject,
  body,
  templateId,
  fromEmail,
  accessToken,
  googleAccessToken,
  provider,
  onClose,
  onSendComplete,
}) => {
  const recipientList = Array.isArray(recipients) ? recipients : [];

  const [selectedIdx, setSelectedIdx]       = useState(0);
  const [previewHtml, setPreviewHtml]       = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [customizations, setCustomizations] = useState({});
  const [editingBody, setEditingBody]       = useState(false);
  const [editDraft, setEditDraft]           = useState('');
  const [sendStatus, setSendStatus]         = useState({});
  const [isSendingAll, setIsSendingAll]     = useState(false);
  const [search, setSearch]                 = useState('');
  const [templateSections, setTemplateSections] = useState([]);
  const [templateLoading, setTemplateLoading]     = useState(false);

  // ── Derived ──────────────────────────────────────────────────────────────
  const filteredRecipients = recipientList.filter(r => {
    const email = typeof r === 'string' ? r : r.email;
    const name  = typeof r === 'object' ? (r.name || '') : '';
    const q     = search.toLowerCase();
    return email.toLowerCase().includes(q) || name.toLowerCase().includes(q);
  });

  const safeIdx          = Math.min(selectedIdx, Math.max(0, filteredRecipients.length - 1));
  const selectedR        = filteredRecipients[safeIdx] || recipientList[0];
  const recipientEmail   = selectedR ? (typeof selectedR === 'string' ? selectedR : selectedR.email) : '';
  const recipientName    = selectedR ? (typeof selectedR === 'object' ? selectedR.name || '' : '') : '';
  const custom           = customizations[recipientEmail] || {};
  const effectiveSubject = custom.subject !== undefined ? custom.subject : subject;
  const effectiveBody    = custom.body    !== undefined ? custom.body    : body;

  const sentCount    = Object.values(sendStatus).filter(s => s === 'sent').length;
  const failedCount  = Object.values(sendStatus).filter(s => s === 'failed').length;
  const pendingCount = recipientList.filter(r => {
    const e = typeof r === 'string' ? r : r.email;
    return !sendStatus[e] || sendStatus[e] === 'idle';
  }).length;

  // ── Helpers ──────────────────────────────────────────────────────────────
  // Returns true only when html is a COMPLETE HTML document (from the visual
  // iframe editor). An AI-rephrased fragment or plain text returns false, and
  // the template wrapper must still be applied via templateId.
  const isFullHtmlDoc = (html) =>
    typeof html === 'string' &&
    (html.trimStart().startsWith('<!DOCTYPE') || html.trimStart().toLowerCase().startsWith('<html'));

  // ── Load personalised preview ────────────────────────────────────────────
  const loadPreview = useCallback(async () => {
    if (!recipientEmail) return;
    setPreviewLoading(true);

    const c = customizations[recipientEmail] || {};
    // Only skip templateId when the custom body is a FULL HTML document
    // (produced by the visual iframe editor). If it's an AI-rephrased fragment
    // or plain text, keep templateId so the server wraps it in the template.
    const customIsFullDoc = c.body !== undefined && isFullHtmlDoc(c.body);
    const payloadTemplateId = customIsFullDoc ? undefined : templateId;
    const sectionOverrides = c.sections || {};

    try {
      const res = await axios.post('/api/email/preview', {
        body: effectiveBody,
        templateId: payloadTemplateId,
        recipientEmail,
        recipientName,
        sectionOverrides,
      });
      setPreviewHtml(res.data.preview || effectiveBody);
    } catch {
      setPreviewHtml(effectiveBody);
    } finally {
      setPreviewLoading(false);
    }
  }, [recipientEmail, recipientName, effectiveBody, templateId, customizations]); // eslint-disable-line

  useEffect(() => {
    if (!editingBody) loadPreview();
  }, [loadPreview, editingBody]);

  // Clear temporary section drafts when not editing
  useEffect(() => {
    if (!editingBody) setSectionDrafts({});
  }, [editingBody]);

  // Load template sections for per-recipient editing
  useEffect(() => {
    if (!templateId) {
      setTemplateSections([]);
      return;
    }
    let cancelled = false;
    const loadTemplate = async () => {
      setTemplateLoading(true);
      try {
        const res = await axios.get(`/api/templates/${templateId}`);
        if (!cancelled) setTemplateSections(res.data?.sections || []);
      } catch (err) {
        console.error('Error loading template sections:', err);
      } finally {
        if (!cancelled) setTemplateLoading(false);
      }
    };
    loadTemplate();
    return () => { cancelled = true; };
  }, [templateId]);

  // ── Send single ──────────────────────────────────────────────────────────
  // Returns true on success, false on failure.
  // When called from sendAll, a false return stops the entire batch.
  const sendToOne = useCallback(async (recipient) => {
    const email       = typeof recipient === 'string' ? recipient : recipient.email;
    const name        = typeof recipient === 'object'  ? recipient.name || '' : '';
    const c           = customizations[email] || {};
    const activeToken = provider === 'gmail' ? googleAccessToken : accessToken;

    // Only skip templateId when the custom body is a complete HTML document
    // (from the visual iframe editor). AI-rephrased fragments keep the templateId
    // so the server wraps them in the full template (header, logo, CTA, footer).
    const customIsFullDoc = c.body !== undefined && isFullHtmlDoc(c.body);
    const payloadTemplateId = customIsFullDoc ? undefined : templateId;

    setSendStatus(prev => ({ ...prev, [email]: 'sending' }));
    try {
      await axios.post('/api/email/send', {
        from:          fromEmail,
        to:            email,
        subject:       c.subject !== undefined ? c.subject : subject,
        body:          c.body !== undefined ? c.body : body,
        templateId:    payloadTemplateId,
        accessToken:   activeToken,
        provider,
        recipientName: name,
        sectionOverrides: c.sections || {},
      });
      setSendStatus(prev => ({ ...prev, [email]: 'sent' }));
      toast.success(`✅ Sent to ${email}`);
      return 'sent';
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message || '';
      setSendStatus(prev => ({ ...prev, [email]: 'failed' }));

      // Unsubscribed is a known, expected condition — show a softer warning and
      // let the batch continue to the next recipient (return 'skip', not 'failed').
      if (errMsg.toLowerCase().includes('unsubscribed')) {
        toast(`⏭️ Skipped ${email} — recipient has unsubscribed`, {
          icon: '📭',
          duration: 5000,
          style: { background: '#fef3c7', color: '#92400e' },
        });
        return 'skip';
      }

      toast.error(`❌ Failed for ${email}: ${errMsg}`, { duration: 6000 });
      return 'failed';
    }
  }, [customizations, accessToken, googleAccessToken, provider, fromEmail, subject, body, templateId]); // eslint-disable-line

  // ── Send all ─────────────────────────────────────────────────────────────
  // 'sent'   → continue
  // 'skip'   → soft skip (unsubscribed etc.) — continue
  // 'failed' → hard error — stop the batch so the user can fix and retry
  const sendAll = async () => {
    setIsSendingAll(true);
    let stopped = false;

    for (const recipient of recipientList) {
      const email = typeof recipient === 'string' ? recipient : recipient.email;
      if (sendStatus[email] === 'sent') continue;

      const result = await sendToOne(recipient);

      if (result === 'failed') {
        // Hard failure — reset remaining recipients to idle so the user can retry
        setSendStatus(prev => {
          const next = { ...prev };
          recipientList.forEach(r => {
            const e = typeof r === 'string' ? r : r.email;
            if (!next[e] || next[e] === 'sending') next[e] = 'idle';
          });
          return next;
        });
        toast.error('⛔ Sending stopped — fix the issue and retry.', { duration: 8000 });
        stopped = true;
        break;
      }
      // 'sent' or 'skip' → carry on

      // Small delay between sends to avoid rate-limiting
      await new Promise(r => setTimeout(r, 120));
    }

    setIsSendingAll(false);
    if (!stopped && onSendComplete) onSendComplete();
  };

  // ── Open editor for selected recipient ──────────────────────────────────
  const openEditor = () => {
    // Always seed the editor with body CONTENT only (not a full HTML document).
    // If effectiveBody happens to be a full doc (saved before this fix), extract body.innerHTML.
    let draft = effectiveBody;
    if (isFullHtmlDoc(effectiveBody)) {
      try {
        const parser = new DOMParser();
        const parsed = parser.parseFromString(effectiveBody, 'text/html');
        draft = parsed.body.innerHTML;
      } catch (_) { /* keep as-is */ }
    }
    setEditDraft(draft);
    const c = customizations[recipientEmail] || {};
    setSectionDrafts({ ...(c.sections || {}) });
    setEditingBody(true);
  };

  // ── Section drafts while editing ─────────────────────────────────────────
  const [sectionDrafts, setSectionDrafts] = useState({});

  const updateSectionDraft = (sectionId, value) => {
    setSectionDrafts(prev => ({ ...prev, [sectionId]: value }));
  };

  // ── Save custom body and section overrides ───────────────────────────────
  // Accepts finalHtml directly from RichBodyEditor (avoids async state race
  // where editDraft might not yet reflect the latest iframe/textarea content).
  const saveCustomBody = (finalHtml) => {
    const bodyValue = (finalHtml !== undefined && finalHtml !== null) ? finalHtml : editDraft;
    setCustomizations(prev => ({
      ...prev,
      [recipientEmail]: {
        ...(prev[recipientEmail] || {}),
        body: bodyValue,
        sections: { ...(prev[recipientEmail]?.sections || {}), ...sectionDrafts },
      },
    }));
    setSectionDrafts({});
    setEditingBody(false);
    toast.success('Custom content saved — preview updated');
  };

  const resetCustom = () => {
    setCustomizations(prev => {
      const next = { ...prev };
      delete next[recipientEmail];
      return next;
    });
    setEditingBody(false);
    toast.success('Reverted to default content');
  };

  const canSend = fromEmail && (accessToken || googleAccessToken);

  // ── Status Avatar ────────────────────────────────────────────────────────
  const StatusAvatar = ({ email, name }) => {
    const st   = sendStatus[email] || 'idle';
    const base = 'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0';
    if (st === 'sent')    return <div className={`${base} bg-green-100 text-green-700`}><FiCheckCircle className="w-3.5 h-3.5" /></div>;
    if (st === 'failed')  return <div className={`${base} bg-red-100 text-red-700`}><FiAlertCircle className="w-3.5 h-3.5" /></div>;
    if (st === 'sending') return <div className={`${base} bg-amber-100 text-amber-700`}><FiRefreshCw className="w-3.5 h-3.5 animate-spin" /></div>;
    return <div className={`${base} bg-primary-100 text-primary-700`}>{(name?.[0] || email[0] || '?').toUpperCase()}</div>;
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-3">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[92vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-primary-600 to-accent-600 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center">
              <FiEye className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-base">Recipients Preview &amp; Send</h2>
              <p className="text-white/70 text-xs">
                {recipientList.length} recipient{recipientList.length !== 1 ? 's' : ''} · Visual customise per recipient · Send directly
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {sentCount   > 0 && <span className="px-2.5 py-1 bg-green-500  text-white text-xs font-bold rounded-lg">{sentCount} sent</span>}
            {failedCount > 0 && <span className="px-2.5 py-1 bg-red-500    text-white text-xs font-bold rounded-lg">{failedCount} failed</span>}
            {pendingCount > 0 && sentCount > 0 && <span className="px-2.5 py-1 bg-white/20 text-white text-xs font-bold rounded-lg">{pendingCount} pending</span>}
            <button onClick={onClose} className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-all ml-2">
              <FiX className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden">

          {/* ── Left: Recipient list ──────────────────────────────────── */}
          <div className="w-72 flex-shrink-0 border-r border-neutral-200 flex flex-col bg-neutral-50">
            <div className="p-3 border-b border-neutral-200">
              <div className="relative">
                <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
                <input
                  type="text"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setSelectedIdx(0); setEditingBody(false); }}
                  placeholder="Search recipients…"
                  className="w-full pl-8 pr-3 py-2 text-xs border border-neutral-300 rounded-lg focus:outline-none focus:border-primary-500 bg-white"
                />
              </div>
              <p className="text-[10px] text-neutral-400 mt-1.5 text-center">
                {filteredRecipients.length} of {recipientList.length} shown
              </p>
            </div>

            <div className="flex-1 overflow-y-auto">
              {filteredRecipients.map((r, idx) => {
                const email    = typeof r === 'string' ? r : r.email;
                const name     = typeof r === 'object' ? (r.name || '') : '';
                const isCustom = !!customizations[email];
                const selected = idx === safeIdx;
                return (
                  <button
                    key={email}
                    onClick={() => { setSelectedIdx(idx); setEditingBody(false); }}
                    className={`w-full text-left px-3 py-2.5 border-b border-neutral-100 transition-all flex items-center gap-2 ${
                      selected
                        ? 'bg-primary-50 border-l-[3px] border-l-primary-500'
                        : 'hover:bg-white border-l-[3px] border-l-transparent'
                    }`}
                  >
                    <StatusAvatar email={email} name={name} />
                    <div className="flex-1 min-w-0">
                      {name && <p className="text-xs font-semibold text-neutral-800 truncate">{name}</p>}
                      <p className="text-xs text-neutral-500 truncate">{email}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      {isCustom && <span className="text-[9px] px-1 py-0.5 bg-violet-100 text-violet-700 rounded font-bold uppercase tracking-wide">Custom</span>}
                      {selected && <FiChevronRight className="w-3 h-3 text-primary-400" />}
                    </div>
                  </button>
                );
              })}
              {filteredRecipients.length === 0 && (
                <p className="text-xs text-neutral-400 text-center py-10">No recipients found</p>
              )}
            </div>

            {/* Send All */}
            <div className="p-3 border-t border-neutral-200 bg-white space-y-2">
              {sentCount > 0 && (
                <div>
                  <div className="flex justify-between text-[10px] text-neutral-500 mb-1">
                    <span>{sentCount} sent</span>
                    <span>{recipientList.length - sentCount} remaining</span>
                  </div>
                  <div className="w-full h-1.5 bg-neutral-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 rounded-full transition-all duration-500"
                      style={{ width: `${(sentCount / recipientList.length) * 100}%` }}
                    />
                  </div>
                </div>
              )}
              <button
                onClick={sendAll}
                disabled={isSendingAll || !canSend || sentCount === recipientList.length}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-accent-600 text-white rounded-xl text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:from-primary-700 hover:to-accent-700 transition-all shadow-medium"
              >
                {isSendingAll          ? <><FiRefreshCw className="w-4 h-4 animate-spin" /> Sending…</>
                  : sentCount === recipientList.length ? <><FiCheckCircle className="w-4 h-4" /> All Sent!</>
                  : <><FiSend className="w-4 h-4" /> Send to All ({pendingCount})</>
                }
              </button>
              {!canSend && (
                <p className="text-[10px] text-amber-600 text-center">⚠️ {!fromEmail ? 'Set "From" email' : 'Sign in to send'}</p>
              )}
            </div>
          </div>

          {/* ── Right: Preview / Editor ────────────────────────────────── */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {selectedR ? (
              <>
                {/* Recipient header */}
                <div className="px-5 py-3 border-b border-neutral-200 bg-white flex items-center justify-between flex-shrink-0 flex-wrap gap-2">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 bg-primary-100 rounded-full flex items-center justify-center text-sm font-bold text-primary-700">
                      {(recipientName?.[0] || recipientEmail[0] || '?').toUpperCase()}
                    </div>
                    <div>
                      {recipientName && <p className="text-sm font-semibold text-neutral-800 leading-tight">{recipientName}</p>}
                      <p className="text-xs text-neutral-500">{recipientEmail}</p>
                    </div>
                    {customizations[recipientEmail] && (
                      <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-xs rounded-lg font-medium">Custom content</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {customizations[recipientEmail] && !editingBody && (
                      <button onClick={resetCustom} className="flex items-center gap-1.5 text-xs px-3 py-1.5 text-neutral-600 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-all">
                        <FiRotateCcw className="w-3 h-3" /> Reset
                      </button>
                    )}
                    <button
                      onClick={editingBody ? () => setEditingBody(false) : openEditor}
                      className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium transition-all ${
                        editingBody
                          ? 'bg-neutral-200 text-neutral-700'
                          : 'bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200'
                      }`}
                    >
                      <FiEdit3 className="w-3.5 h-3.5" />
                      {editingBody ? '← Back to preview' : 'Customise for this recipient'}
                    </button>
                    <button
                      onClick={() => sendToOne(selectedR)}
                      disabled={sendStatus[recipientEmail] === 'sending' || sendStatus[recipientEmail] === 'sent' || !canSend}
                      className="flex items-center gap-1.5 text-xs px-4 py-1.5 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-medium"
                    >
                      {sendStatus[recipientEmail] === 'sending' && <FiRefreshCw className="w-3.5 h-3.5 animate-spin" />}
                      {sendStatus[recipientEmail] === 'sent'    && <FiCheckCircle className="w-3.5 h-3.5" />}
                      {sendStatus[recipientEmail] === 'failed'  && <FiAlertCircle className="w-3.5 h-3.5" />}
                      {(!sendStatus[recipientEmail] || sendStatus[recipientEmail] === 'idle') && <FiSend className="w-3.5 h-3.5" />}
                      {sendStatus[recipientEmail] === 'sending' ? 'Sending…'
                        : sendStatus[recipientEmail] === 'sent'   ? 'Sent ✓'
                        : sendStatus[recipientEmail] === 'failed' ? 'Retry'
                        : 'Send to this recipient'}
                    </button>
                  </div>
                </div>

                {/* Subject row */}
                <div className="px-5 py-2.5 bg-neutral-50 border-b border-neutral-200 flex-shrink-0 flex items-center gap-3">
                  <span className="text-xs font-semibold text-neutral-500 w-14 flex-shrink-0">Subject:</span>
                  <input
                    type="text"
                    value={effectiveSubject}
                    onChange={e => setCustomizations(prev => ({
                      ...prev,
                      [recipientEmail]: { ...(prev[recipientEmail] || {}), subject: e.target.value },
                    }))}
                    className="flex-1 text-sm px-3 py-1.5 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:border-primary-500 transition-all"
                    placeholder="Email subject…"
                  />
                  {custom.subject !== undefined && custom.subject !== subject && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-violet-100 text-violet-600 rounded font-medium flex-shrink-0">Custom</span>
                  )}
                </div>

                {/* Editor or Preview */}
                <div className="flex-1 overflow-y-auto p-5 bg-neutral-100">
                  {editingBody ? (
                    /* ── Rich Body Editor + Section Overrides ── */
                    <div className="max-w-2xl mx-auto space-y-4">
                      <p className="text-xs font-semibold text-violet-800 mb-2.5 flex items-center gap-1.5">
                        <FiEdit3 className="w-3.5 h-3.5" />
                        Editing content for <strong>{recipientName || recipientEmail}</strong>
                        <span className="font-normal text-violet-600">— changes only apply to this recipient</span>
                      </p>
                      <RichBodyEditor
                        value={editDraft}
                        onSaveClose={saveCustomBody}
                      />

                      {templateSections.filter(s => s.id !== 'body').length > 0 && (
                        <div className="border border-violet-200 rounded-xl overflow-hidden bg-white">
                          <div className="px-3 py-2 bg-violet-50 border-b border-violet-200 flex items-center gap-2">
                            <FiEdit3 className="w-3.5 h-3.5 text-violet-700" />
                            <span className="text-xs font-bold text-violet-800">Template sections</span>
                            <span className="text-[10px] text-violet-500">customise other parts of this template for this recipient</span>
                          </div>
                          <div className="p-3 space-y-3">
                            {templateLoading ? (
                              <p className="text-xs text-neutral-500">Loading sections…</p>
                            ) : (
                              templateSections
                                .filter(s => s.id !== 'body')
                                .map(section => {
                                  const currentValue = sectionDrafts[section.id] !== undefined
                                    ? sectionDrafts[section.id]
                                    : (section.value || '');
                                  return (
                                    <div key={section.id}>
                                      <label className="block text-xs font-semibold text-neutral-700 mb-1">
                                        {section.label || section.id}
                                      </label>
                                      {section.type === 'textarea' ? (
                                        <textarea
                                          value={currentValue}
                                          onChange={e => updateSectionDraft(section.id, e.target.value)}
                                          rows={3}
                                          className="w-full px-3 py-2 text-xs border border-neutral-300 rounded-lg focus:outline-none focus:border-primary-500 resize-none"
                                        />
                                      ) : section.type === 'image' ? (
                                        <input
                                          type="text"
                                          value={currentValue}
                                          onChange={e => updateSectionDraft(section.id, e.target.value)}
                                          placeholder="Image URL"
                                          className="w-full px-3 py-2 text-xs border border-neutral-300 rounded-lg focus:outline-none focus:border-primary-500"
                                        />
                                      ) : (
                                        <input
                                          type="text"
                                          value={currentValue}
                                          onChange={e => updateSectionDraft(section.id, e.target.value)}
                                          className="w-full px-3 py-2 text-xs border border-neutral-300 rounded-lg focus:outline-none focus:border-primary-500"
                                        />
                                      )}
                                    </div>
                                  );
                                })
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* ── HTML Preview ── */
                    previewLoading ? (
                      <div className="flex flex-col items-center justify-center h-full gap-3">
                        <div className="w-10 h-10 border-4 border-primary-100 border-t-primary-600 rounded-full animate-spin" />
                        <p className="text-sm text-neutral-500">Generating personalised preview…</p>
                      </div>
                    ) : (
                      <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-medium overflow-hidden">
                        {/* Use iframe so the full HTML document (DOCTYPE + head + style) renders correctly.
                            dangerouslySetInnerHTML strips <html>/<head>/<style> tags, losing all CSS. */}
                        <iframe
                          title="Email Preview"
                          srcDoc={previewHtml || effectiveBody}
                          style={{ width: '100%', minHeight: '480px', border: 'none', display: 'block' }}
                          sandbox="allow-same-origin allow-scripts allow-popups"
                        />
                      </div>
                    )
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-neutral-400">
                <div className="text-center">
                  <FiMail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No recipients added yet</p>
                  <p className="text-sm mt-1">Add recipients in the "To" section first</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailPreview;
